import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { Phase, RoundView, YouView } from "../../services/generated/client.js";
import { flag, type GameLink } from "../../services/gameLink.js";

/**
 * "This year is wrong" — and the prompt it raises on everybody else's phone.
 *
 * # The wording is the feature
 *
 * ADR 0009's whole economy depends on this being pressed by people who are not
 * losing. So it is **free**, it is available to everybody from `Reveal` — not
 * only to whoever was up, because the person who knows the song is not
 * necessarily the person whose turn it was — and it asks
 *
 * > *Is this year wrong?*
 *
 * and never *do you want to void this round?* The second phrasing turns a
 * correction signal into a tactical vote, which is precisely the failure ADR
 * 0009's alternatives section rejected. Nothing in this file may say "void",
 * "cancel" or "replay" on a button.
 *
 * # Over the cap, it still asks and still files
 *
 * `max_voids_per_game` bounds griefing, not listening. Past it the prompt
 * appears, the report is filed, and the copy says the round will stand —
 * because "stop voiding" and "stop collecting corrections" are one line apart
 * and only one of them is the design.
 */
export function FlagPanel({
  link,
  phase,
  round,
  you,
}: {
  link: GameLink;
  /** `reveal` or `dispute` — the flag is live in both. */
  phase: Phase;
  /** The round, for the flag count. */
  round: RoundView;
  /** Your slice, for whether you already raised it. */
  you: YouView | null;
}) {
  const { t } = useTranslation();
  const [year, setYear] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const mine = you?.has_flagged ?? false;
  const raisedByAnybody = round.flags > 0;

  const submit = () => {
    setBusy(true);
    void flag(link, year.length === 4 ? Number(year) : null)
      .then(() => {
        setSent(true);
      })
      .catch(() => {
        // A refusal here is almost always "you have already flagged this card",
        // which costs nothing and is not worth a red box during a reveal.
        setSent(true);
      })
      .finally(() => {
        setBusy(false);
      });
  };

  if (mine || sent) {
    return (
      <section
        data-testid="flag-panel"
        className="rounded-lg border border-white/15 bg-stage-900 p-3"
      >
        <p className="text-sm font-medium">{t("flag.thanks.title")}</p>
        <p className="mt-1 text-xs text-white/50">{t("flag.thanks.lead")}</p>
      </section>
    );
  }

  return (
    <section
      data-testid="flag-panel"
      className="rounded-lg border border-white/15 bg-stage-900 p-3"
    >
      <h2 className="text-sm font-semibold">
        {raisedByAnybody ? t("flag.raised.title") : t("flag.ask.title")}
      </h2>
      <p className="mt-1 text-xs text-white/50">
        {raisedByAnybody ? t("flag.raised.lead") : t("flag.ask.lead")}
      </p>

      <div className="mt-3 flex gap-2">
        <input
          aria-label={t("flag.yearLabel")}
          placeholder={t("flag.yearPlaceholder")}
          inputMode="numeric"
          value={year}
          onChange={(event) => {
            setYear(event.target.value.replace(/\D/g, "").slice(0, 4));
          }}
          className="min-h-12 w-24 rounded-lg border border-white/15 bg-stage-950 px-3 tabular-nums"
        />
        <button
          type="button"
          disabled={busy || phase === "settled"}
          onClick={submit}
          className="min-h-12 flex-1 rounded-lg border border-white/25 px-3 font-medium disabled:opacity-40"
        >
          {raisedByAnybody ? t("flag.raised.action") : t("flag.ask.action")}
        </button>
      </div>

      {round.flags_needed === 0 && raisedByAnybody && (
        // The cap has been reached, or this round has already gathered enough
        // agreement. Either way the honest thing is to say what happens next
        // rather than to hide the button.
        <p className="mt-2 text-xs text-white/40">{t("flag.enough")}</p>
      )}
    </section>
  );
}
